/* eslint-disable jsx-a11y/control-has-associated-label,import/no-unresolved */
import React, { useState, useEffect, useMemo } from 'react';
import { isBrowser, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { MAIN_ICON_COLORS } from 'assets/components';
import BasicContainer from '../../components/BasicContainer';
import { currencies, exchange } from '../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../utilities/formatHelpers';
import { showAlert } from '../../utilities/notifications';
import { useLedgerFilteredWallets } from '../../utilities/sharedHooks/ledgerWallets';
import { RATES_QUERY_KEY } from '../../utilities/sharedQueries';
import AddAccount from '../Accounts/partials/AddAccount';
import { WALLETS_ALL } from '../Accounts/queries';
import ModalLayout from '../shared/ModalLayout';
import InfoPopup from '../shared/Modals/InfoPopup';
import { MODAL_SCREENS } from './constants';
import { useSelectYieldTypes } from './hooks';
import ConfirmDeposit from './modals/ConfirmDeposit';
import YieldDeposit from './modals/Deposit';
import DepositDetails from './modals/DepositDetails';
import ReceivedDetails from './modals/ReceivedDetails';
import YieldFilters from './partials/YieldFilters';
import YieldList from './partials/YieldList';
import { YIELD_DEPOSITED, YIELD_OFFERS, YIELD_RECEIVED, GET_TICKER_MARKET_RATES } from './queries';

const OxygenYield = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('');

  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [selectedYieldType, setSelectedYieldType] = useSelectYieldTypes();

  const [offerDetails, setOfferDetails] = useState({});
  const [depositDetails, setDepositDetails] = useState({});
  const [receivedDetails, setReceivedDetails] = useState({});
  const [confirmedOffer, setConfirmedOffer] = useState({});

  const [wallets, setWallets] = useState([]);

  const DEFAULT_TIMER = process.env.REACT_APP_YIELD_REFRESH_COUNTER || 10;

  const { refetch: refetchAllWallets } = useLedgerFilteredWallets(WALLETS_ALL, {
    enabled: false,
    onSettled(data) {
      setWallets(data);
    },
  });

  const usdsWallet = useMemo(() => {
    if (wallets) {
      return wallets.find((wallet) => wallet.coin === currencies.USDC.fullName.toLowerCase());
    }
    return {};
  }, [wallets]);

  const {
    data: yeildOfferList,
    refetch: refetchYieldOfferList,
    isLoading: isLoadingOfferList,
  } = useQuery(YIELD_OFFERS, {
    enabled: false,
    onSettled: (data, error) => {
      if (data && !error && typeof data === 'string') {
        showAlert({ message: translate('yield_error_msg'), type: 'danger' });
      }
    },
  });

  const {
    data: yeildDepositedList,
    refetch: refetchYieldDepositedList,
    isLoading: isLoadingDepositedList,
  } = useQuery(YIELD_DEPOSITED, {
    enabled: false,
  });

  const {
    data: yeildReceivedList,
    refetch: refetchYieldReceivedList,
    isLoading: isLoadingReceivedList,
  } = useQuery(YIELD_RECEIVED, {
    enabled: false,
  });

  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const allDeposited = useMemo(() => {
    if (yeildDepositedList && Array.isArray(yeildDepositedList)) {
      return yeildDepositedList.reduce(
        (accumulator, deposit) => accumulator + deposit?.depositedAmount,
        0
      );
    }
    return 0;
  }, [yeildDepositedList]);

  const maxAnnualized = useMemo(() => {
    let maxOfferAnn = 0;
    if (yeildOfferList && Array.isArray(yeildOfferList)) {
      yeildOfferList.forEach((offersDateRange) => {
        offersDateRange?.products?.forEach((offer) => {
          if (offer?.annualizedYieldPct > maxOfferAnn) {
            maxOfferAnn = offer?.annualizedYieldPct;
          }
        });
      });
    }

    return formatNumber(maxOfferAnn, exchange.FIAT_VALUE);
  }, [yeildOfferList]);

  const totalAmount = useMemo(() => {
    if (rates && wallets) {
      const userWalletsBalanceInUsdc = wallets.reduce((accumulator, wallet) => {
        const rate =
          rates[`${wallet?.coin?.toUpperCase()}${currencies.USDC.fullName?.toUpperCase()}`]?.ask ||
          0.0;

        const availableBalance =
          wallet?.coin?.toUpperCase() === currencies.USDC.fullName?.toUpperCase()
            ? wallet?.available_balance * currencies.USDC.conversion
            : wallet?.available_balance * rate * currencies.USDC.conversion;
        return accumulator + availableBalance;
      }, 0);

      return userWalletsBalanceInUsdc;
    }
    return 0;
  }, [rates, wallets]);

  const { data: yeildRate, refetch: refetchYieldRate } = useQuery(
    [GET_TICKER_MARKET_RATES, { source: 'BTC', destination: 'USDC' }],
    {
      enabled: false,
    }
  );

  useEffect(() => {
    refetchYieldOfferList();
    refetchYieldDepositedList();
    refetchYieldReceivedList();
    refetchYieldRate();
    refetchAllWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchYieldOfferList();
      refetchYieldDepositedList();
      refetchYieldReceivedList();
      refetchYieldRate();
      refetchAllWallets();
    }, DEFAULT_TIMER * 1000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalBalanceinfoPopupOptions = {
    title: '',
    content: () => (
      <>
        <p className="text-white text-f6">{translate('oxygen_yield_total_balance_info_text')}</p>
      </>
    ),
    isButtonInFooter: false,
    isScrollable: false,
  };

  const showScreen = (screen) => {
    setCurrentScreen(screen);
    setIsModalVisible(true);
  };

  const renderInfoPopup = () => (
    <div
      style={{ left: '55%', top: '-80%' }}
      className="position-absolute shadow px-3 py-3 d-flex flex-row align-items-center  bg-disabled rounded border-1 custom-popup-info popup-info w-75">
      <i className="position-absolute shadow" style={{ top: '30%', left: '-4.5%' }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="15"
          viewBox="0 0 8 15"
          fill="none">
          <path d="M0 7.88574L8 14.8857L8 0.885742L0 7.88574Z" fill={MAIN_ICON_COLORS.INFO_ARROW} />
        </svg>
      </i>
      <p className="text-white text-f6">{translate('oxygen_yield_total_balance_info_text')}</p>
    </div>
  );

  const renderTotalBalance = () => (
    <>
      <div
        className={`d-flex flex-row justify-content-start position-relative ${
          isMobile ? 'align-items-start' : 'align-items-center mt-4'
        }`}>
        <b className="text-f6 text-shadow">{translate('total_balance_label')}</b>
        {!isMobile && (
          <div
            role="button"
            tabIndex={-1}
            onMouseEnter={() => setShowInfoPopup(true)}
            onKeyDown={null}
            onMouseLeave={() => setShowInfoPopup(false)}>
            <i
              className={`icon icon-question-other ${
                showInfoPopup ? 'text-primary' : 'text-shadow'
              }  text-f5 ms-1`}
            />
            {showInfoPopup && renderInfoPopup()}
          </div>
        )}
        {isMobile && (
          <div
            role="button"
            tabIndex={-1}
            onClick={() => showScreen(MODAL_SCREENS.TOTAL_BALANCE_POPUP_INFO)}
            onKeyDown={null}>
            <i
              className={`icon icon-info-icon text-white
            text-f5 ms-1`}
            />
          </div>
        )}
      </div>
      <p className={`text-bold text-f5 ${isMobile ? 'h2 mt-3' : 'h2 mt-1'} `}>
        {`${formatNumberWithComma(formatNumber(totalAmount, exchange.FIAT_VALUE))} ${
          currencies.USDC.fullName
        }`}
      </p>
    </>
  );

  return (
    <div className="container-xl">
      <div className="row mt-5 mb-5">
        {isMobile && (
          <p className="text-dark text-bold text-f4 px-4">{translate('oxygen_yield_tab_label')}</p>
        )}
        <div className="col-md-3 col-sm-12">
          {isBrowser && (
            <>
              <BasicContainer className="bg-lighter mb-3">
                <p className="text-f6 text-shadow text-bold">
                  {translate('available_balance_label')}
                </p>
                <p className="text-bold h2 mt-2">
                  {`${formatNumberWithComma(
                    formatNumber(
                      usdsWallet?.available_balance * currencies.USDC.conversion,
                      exchange.FIAT_VALUE
                    )
                  )} ${currencies.USDC.fullName}`}
                </p>
                {renderTotalBalance()}
                <p className="text-f6 text-shadow text-bold mt-4">
                  {translate('oxygen_yield_all_deposited_label')}
                </p>
                <p className={`text-bold text-f5 ${isMobile ? 'h1 mt-1' : 'h2 mt-1'}`}>
                  {`${formatNumberWithComma(formatNumber(allDeposited, exchange.FIAT_VALUE))} ${
                    currencies.USDC.fullName
                  }`}
                </p>
              </BasicContainer>
              <AddAccount
                label={translate('yield_faq_btn_label')}
                icon="icon-info-icon"
                onClick={() => history.push(`${selectedYieldType}/faq`)}
              />
            </>
          )}
          {isMobile && (
            <div className="mt-4">
              <div className="d-flex flex-row justify-content-start align-items-center px-3">
                <div className="d-flex flex-column me-5">
                  <p className="text-f6 text-shadow text-bold">
                    {translate('available_balance_label')}
                  </p>
                  <p className="text-bold h2 mt-3">
                    {`${formatNumberWithComma(
                      formatNumber(
                        usdsWallet?.available_balance * currencies.USDC.conversion,
                        exchange.FIAT_VALUE
                      )
                    )} ${currencies.USDC.fullName}`}
                  </p>
                </div>
                <div className="d-flex flex-column">{renderTotalBalance()}</div>
              </div>
              <div className="d-flex flex-column px-3">
                <p className="text-f6 text-shadow text-bold mt-4">
                  {translate('oxygen_yield_all_deposited_label')}
                </p>
                <p className={`text-bold ${isMobile ? 'h1 mt-1' : 'h2 mt-2'}`}>
                  {`${formatNumberWithComma(formatNumber(allDeposited, exchange.FIAT_VALUE))} ${
                    currencies.USDC.fullName
                  }`}
                </p>
              </div>
            </div>
          )}
        </div>
        <div
          className={`col-md-9 col-sm-12 pe-0  ${
            isMobile ? 'px-0 rounded-3 hide-scroll mt-3' : ''
          }`}>
          {isBrowser && (
            <div className="col-lg-11 col-md-12 d-md-block d-sm-none bg-lighter rounded-extra px-3 pt-3 pb-4 mb-5">
              <div className="d-flex flex-row justify-content-start align-items-center">
                <img src="./assets/images/yield-banner.png" alt="Banner" />
                <div className="d-flex flex-column justify-content-start ms-2 mt-3">
                  <p className="test-dark text-f4 text-bold">
                    <span>{translate('yield_note_title_part_1')}</span>
                    <span className="text-primary mx-1">
                      {translate('yield_note_title_part_2').replace('__MAX_ANN__', maxAnnualized)}
                    </span>
                    <span>{translate('yield_note_title_part_3')}</span>
                  </p>
                  <p className="text-dark text-f5 py-3">
                    <span className="text-primary me-3">1</span>
                    {translate('yield_step_1')}
                  </p>
                  <p className="text-dark text-f5 pb-3">
                    <span className="text-primary me-3">2</span>
                    {translate('yield_step_2')}
                  </p>
                  <p className="text-dark text-f5">
                    <span className="text-primary me-3">3</span>
                    {translate('yield_step_3')}
                  </p>
                </div>
              </div>
            </div>
          )}
          <YieldFilters
            rate={yeildRate}
            depositedList={yeildDepositedList}
            receivedList={yeildReceivedList}
            onChangeType={setSelectedYieldType}
            selectedTab={selectedYieldType}
          />
          <YieldList
            offerList={yeildOfferList}
            depositedList={yeildDepositedList}
            receivedList={yeildReceivedList}
            type={selectedYieldType}
            isOffersLoading={isLoadingOfferList}
            isDepositedLoading={isLoadingDepositedList}
            isReceivedLoading={isLoadingReceivedList}
            showScreen={showScreen}
            setDetails={setOfferDetails}
            setDepositDetails={setDepositDetails}
            setReceivedDetails={setReceivedDetails}
          />
        </div>
      </div>
      <ModalLayout
        onHide={() => setIsModalVisible(false)}
        isVisible={isModalVisible}
        modalHeight={
          currentScreen !== MODAL_SCREENS.CONFRIM_DEPOSIT && currentScreen !== MODAL_SCREENS.DEPOSIT
            ? 'h-auto'
            : ''
        }
        currentScreen={currentScreen}>
        <InfoPopup
          keyName={MODAL_SCREENS.TOTAL_BALANCE_POPUP_INFO}
          options={totalBalanceinfoPopupOptions}
        />
        <YieldDeposit
          keyName={MODAL_SCREENS.DEPOSIT}
          rate={yeildRate}
          details={offerDetails}
          openScreen={showScreen}
          setConfirmedOffer={setConfirmedOffer}
          wallet={usdsWallet}
        />
        <ConfirmDeposit
          keyName={MODAL_SCREENS.CONFRIM_DEPOSIT}
          hide={() => setIsModalVisible(false)}
          openScreen={showScreen}
          offerDetails={confirmedOffer}
          setConfirmedOffer={setConfirmedOffer}
          refetchDeposited={refetchYieldDepositedList}
          refrechWallets={refetchAllWallets}
          selectedYieldType={selectedYieldType}
        />
        <DepositDetails
          keyName={MODAL_SCREENS.DEPOSIT_DETAILS}
          details={depositDetails}
          rate={yeildRate}
        />
        <ReceivedDetails
          keyName={MODAL_SCREENS.RECEIVED_DETAILS}
          details={receivedDetails}
          rate={yeildRate}
        />
      </ModalLayout>
    </div>
  );
};

export default OxygenYield;
